import { useToastManager } from "@porsche-design-system/components-react"
import type { VisibilityEditView, VisibilityView } from "@slfinrtl/ui-bff-routes"
import { getVisibilityEditViewRoute, getVisibilityRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useRef, useState } from "react"
import { useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import { AnimatedStage, visibilityFieldMessages } from "src/components"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useBff, useConfig } from "src/hooks"
import { useScrollToStage } from "src/screens/vehicleDetails/common/useScrollToStage/useScrollToStage"
import { useVehicleDetailsContextForStage } from "../../context/VehicleDetailsContext"
import { useEditActionButton } from "../../hooks/useEditActionButton"
import { Visibility } from "./components/Visibility"
import { VisibilityFormContainer } from "./visibilityFormContainer/VisibilityFormContainer"

export const VisibilityContainer = withErrorHandler(() => {
	const { formatMessage } = useIntl()
	const [visibilityView, setVisibilityView] = useState<VisibilityView>()
	const { inventoryId, mode, notify, setEditStage } = useVehicleDetailsContextForStage("visibility")
	const { onlineOrderingEnabled } = useConfig()
	const { addMessage } = useToastManager()

	const fetchView = useBff(
		getVisibilityRoute,
		{
			body: undefined,
			pathParams: { inventoryId },
			queryParams: {},
		},
		"inline",
	)

	const ref = useRef(null)

	useEffect(() => {
		fetchView().then(setVisibilityView)
	}, [fetchView])

	useEffect(() => {
		if (visibilityView) {
			notify({ event: "visibilityChanged", payload: visibilityView })
		}
	}, [visibilityView, notify])

	const fetchEditView = useBff(
		getVisibilityEditViewRoute,
		{
			body: undefined,
			pathParams: { inventoryId },
			queryParams: {},
		},
		"inline",
	)

	const [editView, setEditView] = useState<VisibilityEditView | undefined>()
	const [isEditViewLoading, setIsEditViewLoading] = useState(false)

	const handleEditClick = useCallback((): Promise<void> => {
		setIsEditViewLoading(true)
		return fetchEditView()
			.then((result) => {
				setEditView(result)
				setEditStage("visibility")
			})
			.finally(() => setIsEditViewLoading(false))
	}, [fetchEditView, setEditStage])

	useScrollToStage({
		callbackFn: handleEditClick,
		editStage: "visibility",
		ref,
	})

	const onExit = useCallback(() => {
		setEditStage(undefined)
	}, [setEditStage])

	const onSaved = useCallback(
		(view: VisibilityView) => {
			setVisibilityView(view)
			onExit()
			notify({ event: "visibilityChanged", payload: view })
			notify({ event: "visibilityUpdated", payload: view })
			addMessage({
				state: "success",
				text: formatMessage(commonMessages.saveSuccess),
			})
		},
		[addMessage, formatMessage, setVisibilityView, onExit, notify],
	)

	const actionButton = useEditActionButton("visibility", visibilityView?.editAction, {
		fetch: handleEditClick,
		isLoading: isEditViewLoading,
	})

	return (
		<AnimatedStage ref={ref} title={formatMessage(visibilityFieldMessages.title)} actionButtons={actionButton}>
			<ErrorNotificationInline dismissButton={false} />
			{mode !== "edit" || !editView ? (
				visibilityView && <Visibility view={visibilityView} isOnlineOrderingEnabled={onlineOrderingEnabled} />
			) : (
				<VisibilityFormContainer
					inventoryId={inventoryId}
					editView={editView}
					onCancel={onExit}
					onSaved={onSaved}
				/>
			)}
		</AnimatedStage>
	)
})
